import React, { createContext, useContext, useState, useEffect } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useFetchOrganizationSettings } from '@/service/Settings/SettingsQueries';
import { useFetchApplicationFeatures } from '@/service/ApplicationQueries';

// Enable plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Create a context with default values
const TimeContext = createContext({
  currentTime: dayjs().format(),
  timezone: dayjs.tz.guess(),
});

// Custom hook to use the TimeContext
export const useTime = () => useContext(TimeContext);

// TimeProvider component that wraps its children with the TimeContext provider
export const TimeProvider: React.FC = ({ children }) => {
  // Fetch organization settings and application features
  const { data: GeneralSettings, isLoading, isError } = useFetchOrganizationSettings();
  const { data: appFeature } = useFetchApplicationFeatures();

  // State to store the current time and timezone
  const [currentTime, setCurrentTime] = useState(dayjs().format());
  const [timezone, setTimezone] = useState(dayjs.tz.guess());


  // Effect to update timezone based on settings and features
  useEffect(() => {
    if (appFeature?.ServerUtcTime && GeneralSettings?.Timezone) {
      setTimezone(GeneralSettings?.Timezone); // Use timezone from settings if ServerUtcTime is true
    } else {
      setTimezone(dayjs.tz.guess()); // Default to local machine timezone
    }
  }, [appFeature, GeneralSettings]);

  // Effect to update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      if (appFeature?.ServerUtcTime && GeneralSettings?.Timezone) {
        setCurrentTime(dayjs().tz(GeneralSettings?.Timezone).format()); // Use server timezone
      } else {
        setCurrentTime(dayjs().format()); // Use local machine time
      }
    }, 1000);

    return () => clearInterval(interval); // Cleanup interval on component unmount
  }, [appFeature, GeneralSettings]);

  return (
    <TimeContext.Provider value={{ currentTime, timezone }}>
      {children}
    </TimeContext.Provider>
  );
};
